import { useState, useMemo, useRef } from "react";
import { useQuizContext } from "@/state/QuizContext";
import { clearSavedQuiz } from "@/domains/storage/QuizSave";
import { useResultsStep } from "@/state/useResultsStep";
import { useProducts } from "@/state/ProductsContext";
import type { RawProduct, RawVariant, RawSellingPlanAllocation } from "@/state/ProductsContext";
import { calculateDailyIntake } from "@/domains/nutrition/DailyIntake";
import { isPlanLocked } from "@/helpers/plansToPacks";
import { quizTestTags, quizTestProperties } from "@/experiments/quizTests";
import { isDogIncluded } from "@/helpers/dogInclusion";
import { buildDogForStorage } from "@/helpers/enums";
import { buildHiddenPriceMap } from "./hiddenPlanPricing";
import { GTM } from "@/services/gtm";
import { fromYmd, isDisabledDate } from "./deliveryDates/datesHelper";
import type { DogForm } from "@/helpers/types";

type CartLineItem = {
  id: number;
  quantity: number;
  properties: Record<string, string>;
  selling_plan?: number | string;
};

type MealSelectionItem = { variantId: string; count: number };

const APP_URL = "https://paws-price-app.paws-on-plates.workers.dev";
const FETCH_TIMEOUT_MS = 15000;

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeoutMs: number = FETCH_TIMEOUT_MS
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error("Request timed out. Please try again.");
    }
    throw error;
  }
}

async function fetchCartWithRetry(
  url: string,
  options: RequestInit = {},
  maxRetries: number = 3
): Promise<Response> {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetchWithTimeout(url, options);

      if (response.status === 409) {
        // 409 Conflict — retry silently
        if (attempt < maxRetries) {
          await new Promise((r) => setTimeout(r, 300 * attempt));
          continue;
        }
      }

      return response;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      console.error(`[fetchCartWithRetry] Attempt ${attempt}/${maxRetries} failed:`, error);

      if (attempt < maxRetries) {
        await new Promise((r) => setTimeout(r, 300 * attempt));
      }
    }
  }

  throw lastError || new Error("Cart operation failed after retries");
}

async function readCart(): Promise<{ items: RawCartItem[] } | null> {
  try {
    const res = await fetchWithTimeout("/cart.js", { headers: { Accept: "application/json" } });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

type RawCartItem = {
  key: string;
  variant_id: number;
  quantity: number;
  properties?: Record<string, string> | null;
};

function cartHasLine(cart: { items: RawCartItem[] } | null, item: CartLineItem): boolean {
  if (!cart?.items) return false;
  const dogId = item.properties?._paws_dog_id;
  return cart.items.some(
    (it) =>
      String(it.variant_id) === String(item.id) &&
      (it.properties?._paws_dog_id ?? "") === (dogId ?? "")
  );
}

type CartAddOutcome = { ok: true } | { ok: false; status?: number; text?: string };

// /cart/add.js is NOT idempotent: a POST that commits server-side but times
// out client-side must never be blindly re-sent (that is how carts end up
// with duplicated plan/treat lines and customers with duplicate Recharge
// subscriptions). On any failure we read the cart back and only re-send the
// lines that verifiably did not land.
async function addLinesVerified(items: CartLineItem[], maxAttempts = 3): Promise<CartAddOutcome> {
  let pending = items;
  let lastStatus: number | undefined;
  let lastText = "";

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    let outcomeUnknown = false;
    try {
      const res = await fetchWithTimeout("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: pending }),
      });
      if (res.ok) return { ok: true };
      lastStatus = res.status;
      lastText = await res.text().catch(() => "");
      if (res.status !== 409 && res.status < 500) {
        // Definite rejection (bad variant, sold out, ...) — the same payload
        // will keep failing; surface the error.
        return { ok: false, status: res.status, text: lastText };
      }
    } catch (error) {
      // Timeout / network drop: the add may or may not have committed.
      outcomeUnknown = true;
      lastText = error instanceof Error ? error.message : String(error);
    }

    const cart = await readCart();
    if (cart) {
      pending = pending.filter((it) => !cartHasLine(cart, it));
      if (pending.length === 0) return { ok: true };
    } else if (outcomeUnknown) {
      // We cannot verify what landed; re-POSTing blindly risks duplicates.
      return { ok: false, text: lastText || "cart unreachable after add failure" };
    }

    if (attempt < maxAttempts) {
      await new Promise((r) => setTimeout(r, 300 * attempt));
    }
  }

  return { ok: false, status: lastStatus, text: lastText };
}

// Final safety net before redirecting to /checkout: if concurrent submissions
// or historical state still produced more than one line for the same
// (variant, dog), remove the extras so one checkout can never create
// duplicate subscriptions.
async function removeDuplicateQuizLines(): Promise<void> {
  const cart = await readCart();
  if (!cart?.items) return;

  const seen = new Set<string>();
  for (const it of cart.items) {
    const dogId = it.properties?._paws_dog_id;
    if (!dogId) continue;
    const key = `${it.variant_id}:${dogId}`;
    if (!seen.has(key)) {
      seen.add(key);
      continue;
    }
    try {
      await fetchWithTimeout("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: it.key, quantity: 0 }),
      });
      console.error("[useCheckout] Removed duplicate cart line", key);
    } catch (error) {
      console.error("[useCheckout] Failed to remove duplicate cart line", key, error);
    }
  }
}

function parseGramsFromTitle(title: string): number | null {
  const str = String(title).trim();
  const validSizes = [250, 400, 600, 800];

  for (const size of validSizes) {
    if (str === String(size) || str.toLowerCase() === `${size}g`) {
      return size;
    }
  }

  const gramsMatch = str.match(/\b(\d+)\s*g\b/i);
  if (gramsMatch) {
    const num = Number(gramsMatch[1]);
    if (validSizes.includes(num)) return num;
  }

  const justNumber = parseInt(str, 10);
  if (!isNaN(justNumber) && validSizes.includes(justNumber)) {
    return justNumber;
  }

  // Could not parse grams — return null
  return null;
}

function findSellingPlanByDays(
  variant: RawVariant,
  planDays: number
): number | string | null {
  const daysStr = String(planDays);
  const allocations = variant.selling_plan_allocations || [];

  const match = allocations.find((spa: RawSellingPlanAllocation) =>
    sellingPlanNameMatchesDays(spa.selling_plan_name, daysStr)
  );

  if (match) {
    return match.selling_plan_id;
  }

  return null;
}

// Word-bounded match so "7 day" cannot match a plan named "27 day".
function sellingPlanNameMatchesDays(name: string | undefined, daysStr: string): boolean {
  return new RegExp(`(^|[^0-9])${daysStr}\\s*day`, "i").test(name || "");
}

function inferPlanDaysFromProduct(product: RawProduct): number | null {
  const title = String(product.title ?? "").toLowerCase();
  const handle = String(product.handle ?? "").toLowerCase();
  const combined = `${title} ${handle}`;

  for (const days of [7, 14, 28]) {
    if (combined.includes(`${days}-day`) || combined.includes(`${days} day`)) {
      return days;
    }
  }

  return null;
}

function findPlanVariant(
  planProducts: RawProduct[],
  planDays: number,
  grams: number,
  planProductMap: Record<string, string>
): { variant: RawVariant | null; sellingPlanId: number | string | null } {
  const hasIdMap = planProductMap && Object.keys(planProductMap).length > 0;

  let planProduct: RawProduct | undefined;

  if (hasIdMap) {
    const productId = planProductMap[String(planDays)];
    if (productId) {
      planProduct = planProducts.find((p) => String(p.id) === productId);
    }
  }

  if (!planProduct) {
    planProduct = planProducts.find((p) => inferPlanDaysFromProduct(p) === planDays);
    if (planProduct) {
      // Fallback string matching used
    }
  }

  if (!planProduct || !planProduct.variants?.length) {
    console.error("[findPlanVariant] No plan product found for", planDays, "days. Map:", planProductMap, "Available:",
      planProducts.map(p => ({ id: p.id, title: p.title, handle: p.handle }))
    );
    return { variant: null, sellingPlanId: null };
  }

  const chosenVariant: RawVariant | undefined = planProduct.variants.find(
    (v) => parseGramsFromTitle(v.title) === grams
  );

  if (!chosenVariant) {
    console.error(
      "[findPlanVariant] No variant found for", grams, "g. Available variants:",
      planProduct.variants.map((v) => ({ id: v.id, title: v.title }))
    );
    return { variant: null, sellingPlanId: null };
  }

  const sellingPlanId = findSellingPlanByDays(chosenVariant, planDays);

  return {
    variant: chosenVariant,
    sellingPlanId,
  };
}

function findVariantSellingPlanForDays(
  products: RawProduct[],
  variantId: string | number,
  planDays: number
): number | string | null {
  const variantIdStr = String(variantId);
  const daysStr = String(planDays);

  for (const product of products) {
    for (const variant of product.variants || []) {
      if (String(variant.id) !== variantIdStr) continue;

      const allocations = variant.selling_plan_allocations || [];
      const match = allocations.find((spa: RawSellingPlanAllocation) =>
        sellingPlanNameMatchesDays(spa.selling_plan_name, daysStr)
      );
      return match?.selling_plan_id ?? null;
    }
  }

  return null;
}

function computeRecommendedPacksFromForm(form: Record<string, unknown>): number {
  const weightKg = Number(form?.weightKg) || 0;
  const ageYears = Number(form?.ageYears ?? form?.years) || 0;
  const bodyConditionRaw = (form?.bodyCondition || "IDEAL").toString();
  const activityLevelRaw = (form?.activityLevel || "MEDIUM").toString();

  const bodyCondition = bodyConditionRaw.toLowerCase() as "underweight" | "ideal" | "overweight";
  const activityLevel = activityLevelRaw.toLowerCase() as "low" | "medium" | "high";

  if (!weightKg) return 0;

  const intake = calculateDailyIntake({
    weightKg,
    ageYears,
    bodyCondition: bodyCondition || "ideal",
    activityLevel: activityLevel || "medium",
  });

  const gramsPerDay = intake.gramsPerDay;
  const planDays = Number(form?.selectedPlanDays) || 7;
  const totalGrams = gramsPerDay * planDays;

  const basePackSize = Number(form?.selectedPackSize) || 400;
  if (!basePackSize) return 0;

  const packs = Math.ceil(totalGrams / basePackSize);
  return Math.max(1, packs);
}

/**
 * @param reviewStep results step that hosts the delivery-date picker (control
 *   quiz: 5; Price & Value variant, which adds the supplement step: 6).
 */
export function useCheckout(reviewStep: number = 5) {
  const { state } = useQuizContext();
  const { setResultsStep } = useResultsStep();
  const {
    planProducts,
    mealProducts,
    treatProducts,
    hiddenPriceProducts,
    planProductMap,
    hiddenPriceMap: hiddenPriceIdMap,
  } = useProducts();

  const hiddenPriceMap = useMemo(() => {
    return buildHiddenPriceMap(hiddenPriceProducts ?? [], hiddenPriceIdMap);
  }, [hiddenPriceProducts, hiddenPriceIdMap]);

  const [submitting, setSubmitting] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  // Synchronous re-entrancy lock: React state updates are async, so a second
  // click/touch in the same frame would start a second full clear→add run.
  const checkoutInFlightRef = useRef(false);

  const hasPlanProducts = Array.isArray(planProducts) && planProducts.length > 0;

  const { mealsUnderRecommended, usedPacks, recommendedPacks } = useMemo(() => {
    // Issue #50: gate on the first INCLUDED dog — an excluded dog's meal count
    // must never block (or unblock) checkout.
    const dogId =
      state.dogOrder.find((id) => isDogIncluded(state.dogForms?.[id])) ??
      state.dogOrder[0];
    if (!dogId) return { mealsUnderRecommended: true, usedPacks: 0, recommendedPacks: 0 };

    const form = state.dogForms[dogId] || {};
    const recommended = Number(form.mealRecommendedPacks) || computeRecommendedPacksFromForm(form);
    const mealSelection = Array.isArray(form.mealSelection) ? form.mealSelection : [];
    const usedFromSelection = mealSelection.reduce(
      (sum: number, item: MealSelectionItem) => sum + (Number(item?.count) || 0),
      0
    );
    const used = usedFromSelection || Number(form.mealSelectedPacks) || 0;

    if (!recommended || recommended <= 0) {
      return { mealsUnderRecommended: true, usedPacks: used, recommendedPacks: 0 };
    }

    if (!mealSelection.length || used <= 0) {
      return { mealsUnderRecommended: true, usedPacks: used, recommendedPacks: recommended };
    }

    return {
      mealsUnderRecommended: used < recommended,
      usedPacks: used,
      recommendedPacks: recommended,
    };
  }, [state.dogOrder, state.dogForms]);

  const goToCheckout = async () => {
    if (checkoutInFlightRef.current) return;
    checkoutInFlightRef.current = true;
    setSubmitting(true);
    setCheckoutError(null);

    try {
      if (!state.dogOrder.length) {
        console.error("[useCheckout] No dogs found in dogOrder");
        setCheckoutError("No dogs found. Please complete the quiz first.");
        setSubmitting(false);
        return;
      }

      const firstForm = (state.dogForms?.[state.dogOrder[0]] ?? {}) as Partial<DogForm> & { deliveryDate?: string; shipDate?: string };
      const deliveryDate: string | undefined = state.deliveryDate || firstForm.deliveryDate;
      const shipDate: string | undefined = state.shipDate || firstForm.shipDate;
      const now = new Date();

      if (!deliveryDate) {
        setDateError("Please select a delivery date before checkout.");
        setResultsStep(reviewStep);
        setSubmitting(false);
        return;
      }

      const deliveryDateObj = fromYmd(deliveryDate);

      if (!deliveryDateObj) {
        console.error("[useCheckout] Invalid delivery date format:", deliveryDate);
        setDateError("Invalid delivery date. Please select a new date.");
        setResultsStep(reviewStep);
        setSubmitting(false);
        return;
      }

      if (isDisabledDate(deliveryDateObj, now)) {
        setDateError("Your selected delivery date is no longer available. Please choose a new date.");
        setResultsStep(reviewStep);
        setSubmitting(false);
        return;
      }

      if (!hasPlanProducts) {
        console.error("[useCheckout] No planProducts loaded");
        setCheckoutError("Unable to load plan options. Please refresh the page and try again.");
        setSubmitting(false);
        return;
      }

      const variantLookup: Record<string, { productTitle: string; variantTitle: string; price: number }> = {};
      for (const product of mealProducts ?? []) {
        for (const variant of product.variants ?? []) {
          variantLookup[String(variant.id)] = {
            productTitle: String(product.title ?? ""),
            variantTitle: String(variant.title ?? ""),
            price: parseFloat(String(variant.price ?? "0")) || 0,
          };
        }
      }

      const allLookupProducts: RawProduct[] = [
        ...(mealProducts ?? []),
        ...(treatProducts ?? []),
      ];

      // POST, not GET: a GET can be served from cache and silently no-op,
      // leaving the previous run's lines in the cart. Clearing is idempotent,
      // so the retry wrapper is safe here.
      const clearRes = await fetchCartWithRetry("/cart/clear.js", {
        method: "POST",
        headers: { Accept: "application/json" },
      });
      if (!clearRes.ok) {
        console.error("[useCheckout] Failed to clear cart", clearRes.status);
        setCheckoutError("Unable to prepare your cart. Please refresh and try again.");
        setSubmitting(false);
        return;
      }

      const allTreatLineItems: CartLineItem[] = [];
      let totalOrderValue = 0;

      // Defensive dedupe: persisted quiz state has historically been able to
      // accumulate repeated dog ids; each id may only produce one plan line.
      // Issue #50: excluded dogs never reach the cart — this filter is the
      // single boundary that keeps them out of Recharge subscriptions and
      // fulfilment exports downstream.
      const dogIds = Array.from(new Set(state.dogOrder)).filter((id) =>
        isDogIncluded(state.dogForms?.[id])
      );

      if (!dogIds.length) {
        console.error("[useCheckout] Every dog is switched out of the box");
        setCheckoutError("Please include at least one dog in this box before checkout.");
        setSubmitting(false);
        return;
      }

      // Quiz tests (#51): the arm + signup cadence ride every subscription line
      // (Recharge copies line-item properties onto the subscription) and the
      // order attributes. Empty for non-participants and QA overrides.
      const quizTests = quizTestTags(window.__PAWS_EXPERIMENTS__);

      for (const dogId of dogIds) {
        const form = (state.dogForms?.[dogId] ?? {}) as Partial<DogForm> & Record<string, unknown>;

        const planDays = Number(form.selectedPlanDays);
        if (!planDays) {
          console.error("[useCheckout] selectedPlanDays missing for dog", dogId);
          setCheckoutError(`Please select a plan duration for ${form.name || "your dog"}.`);
          setSubmitting(false);
          return;
        }

        // Backstop for the temporary plan lock: a stale saved session that
        // never revisited the plan step could still carry a locked cadence.
        // Selecting on the plan step is impossible, so send them back there.
        if (isPlanLocked(planDays)) {
          console.error("[useCheckout] Locked plan length in saved state", planDays, "for dog", dogId);
          setCheckoutError(
            `The ${planDays} day plan is temporarily unavailable for new orders — please choose a 7 or 14 day plan.`
          );
          setResultsStep(2);
          setSubmitting(false);
          return;
        }

        const packSizeGrams = Number(form.selectedPackSize);
        if (!packSizeGrams) {
          console.error("[useCheckout] selectedPackSize missing for dog", dogId);
          setCheckoutError(`Please select a pack size for ${form.name || "your dog"}.`);
          setSubmitting(false);
          return;
        }

        const { variant: planVariant, sellingPlanId: bundleSellingPlanId } = findPlanVariant(
          planProducts,
          planDays,
          packSizeGrams,
          planProductMap
        );

        if (!planVariant) {
          console.error("[useCheckout] No plan variant found for dog", dogId, { planDays, packSizeGrams });
          setCheckoutError(`Unable to find a ${packSizeGrams}g plan for ${form.name || "your dog"}. Please try a different pack size.`);
          setSubmitting(false);
          return;
        }

        const variantGrams = parseGramsFromTitle(planVariant?.title || "");
        if (variantGrams !== packSizeGrams) {
          console.error(
            "[useCheckout] PACK SIZE MISMATCH for dog", dogId,
            "! Expected", packSizeGrams, "g but variant is", variantGrams, "g"
          );
          setCheckoutError(`Pack size mismatch for ${form.name || "your dog"}. Please refresh and try again.`);
          setSubmitting(false);
          return;
        }

        if (!bundleSellingPlanId) {
          console.error(
            "[useCheckout] No selling plan found for variant", planVariant.id,
            "with", planDays, "days. Variant:", planVariant.title
          );
          setCheckoutError(`Subscription plan not available for ${packSizeGrams}g. Please try a different pack size.`);
          setSubmitting(false);
          return;
        }

        const boxVariantId = planVariant.id;
        const rawMealSelection = (form.mealSelection ?? []) as MealSelectionItem[];
        const mealSelection = Array.isArray(rawMealSelection) ? rawMealSelection : [];

        const totalPacks =
          Number(form.mealSelectedPacks) ||
          mealSelection.reduce((sum, item) => sum + (Number(item?.count) || 0), 0);

        if (totalPacks <= 0) {
          console.error("[useCheckout] totalPacks is 0 for dog", dogId);
          setCheckoutError(`Please select at least one meal for ${form.name || "your dog"}.`);
          setSubmitting(false);
          return;
        }

        const mealMode = (form.mealMode === "MIXED" ? "MIXED" : "FULL") as "FULL" | "MIXED";

        const hiddenPriceForDays = hiddenPriceMap[planDays as 7 | 14 | 28];
        const rawPricePerPack = Number(hiddenPriceForDays?.[packSizeGrams as 250 | 400 | 600 | 800] ?? 0);

        if (!Number.isFinite(rawPricePerPack) || rawPricePerPack <= 0) {
          console.error("[useCheckout] Missing hidden pricePerPack for dog", dogId);
          setCheckoutError("Unable to calculate pricing. Please refresh and try again.");
          setSubmitting(false);
          return;
        }

        const pricePerPack = round2(rawPricePerPack);
        const totalPrice = round2(rawPricePerPack * totalPacks);
        totalOrderValue += totalPrice;

        const mealSummaryParts: string[] = [];
        for (const entry of mealSelection) {
          const variantId = String(entry?.variantId || "");
          const count = Number(entry?.count || 0);
          if (!variantId || count <= 0) continue;

          const meta = variantLookup[variantId];
          if (!meta || !meta.productTitle) {
            console.error("[useCheckout] Could not find product info for variant:", variantId);
            setCheckoutError("We couldn't identify one of your selected meals. Please refresh and try again, or contact support@pawsonplates.co.uk.");
            setSubmitting(false);
            return;
          }

          mealSummaryParts.push(`${count}× ${meta.productTitle} ${meta.variantTitle}`);
        }
        const mealsSummary = mealSummaryParts.length ? mealSummaryParts.join(", ") : "";

        const dogProfilePayload = {
          ...buildDogForStorage({ ...form, id: dogId }),
          ownerName: form.ownerName,
          ownerEmail: form.ownerEmail,
          ownerPhone: form.ownerPhone,
          marketingOptIn: form.marketingOptIn === true,
        };

        const appRes = await fetchWithTimeout(`${APP_URL}/api/quiz/box`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            boxVariantId,
            totalPrice,
            dogName: form.name || "",
            planDays,
            deliveryDate,
            shipDate,
            mealMode,
            totalPacks,
            packSizeGrams,
            pricePerPack,
          }),
        });

        if (!appRes.ok) {
          const text = await appRes.text().catch(() => "");
          console.error("[useCheckout] Worker error for dog", dogId, appRes.status, text);
          setCheckoutError("Unable to process your order. Please try again in a moment.");
          setSubmitting(false);
          return;
        }

        const planLine: CartLineItem = {
          id: Number(boxVariantId),
          quantity: 1,
          properties: {
            ...(mealsSummary && { Meals: mealsSummary }),
            _paws_quiz_source: "paws_quiz_v1",
            _paws_dog_id: dogId,
            _paws_box_variant_id: String(boxVariantId),
            _paws_plan_days: String(planDays),
            _paws_pack_size_grams: String(packSizeGrams),
            _paws_meal_mode: mealMode,
            _paws_total_packs: String(totalPacks),
            _paws_price_per_pack: String(pricePerPack),
            _paws_total_price: String(totalPrice),
            _paws_dog_json: JSON.stringify(dogProfilePayload),
            _paws_meals_json: JSON.stringify(
              mealSelection.map((item) => {
                const meta = variantLookup[String(item.variantId)];
                return {
                  variantId: item.variantId,
                  count: item.count,
                  productTitle: meta?.productTitle || "",
                  variantTitle: meta?.variantTitle || "",
                  price: pricePerPack,
                };
              })
            ),
            ...(deliveryDate && {
              __qivora_delivery_date: deliveryDate,
            }),
            ...(shipDate && {
              __qivora_ship_date: shipDate,
            }),
            ...quizTestProperties(quizTests, planDays),
          },
          ...(bundleSellingPlanId && { selling_plan: bundleSellingPlanId }),
        };

        const planAdd = await addLinesVerified([planLine]);
        if (!planAdd.ok) {
          console.error("[useCheckout] Failed to add plan item for dog", dogId, planAdd.status, planAdd.text);
          setCheckoutError(`Unable to add plan for ${form.name || "your dog"}. Please try again.`);
          setSubmitting(false);
          return;
        }

        const dogTreats = (state.treatSelections?.[dogId] ?? {}) as Record<string, number>;

        for (const [variantId, count] of Object.entries(dogTreats)) {
          const qty = Number(count);
          if (!variantId || qty <= 0) continue;

          const treatPlanId = findVariantSellingPlanForDays(allLookupProducts, variantId, planDays);

          const treatItem: CartLineItem = {
            id: Number(variantId),
            quantity: qty,
            properties: {
              Plan: `${planDays} Day Plan`,
              _paws_quiz_source: "paws_quiz_v1",
              _paws_dog_id: dogId,
              ...quizTestProperties(quizTests, planDays),
            },
            ...(treatPlanId && { selling_plan: treatPlanId }),
          };

          allTreatLineItems.push(treatItem);
        }

      }

      // Meta browser identifiers: written onto the cart so they reach order
      // note_attributes, where the server-side tracking pipeline (paws-admin)
      // reads them for CAPI purchase attribution.
      const readCookie = (name: string): string => {
        const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
        return m ? decodeURIComponent(m[1]) : "";
      };
      const fbp = readCookie("_fbp");
      const fbc = readCookie("_fbc");

      // Experiment assignments (set synchronously by snippets/paws-experiments
      // .liquid) ride the same rail: cart attribute -> order note_attributes,
      // where paws-admin's attribution parser reconciles visitor -> customer.
      // Compact shape {"key":{"v":1,"a":"variant"}}; QA overrides excluded;
      // dropped entirely if it would exceed 500 chars.
      let pawsExperiments = "";
      const expStore = window.__PAWS_EXPERIMENTS__;
      if (expStore?.assignments) {
        const compact: Record<string, { v: number; a: string }> = {};
        for (const [expKey, rec] of Object.entries(expStore.assignments)) {
          if (rec && rec.variant && rec.src !== "qa_override") {
            compact[expKey] = { v: rec.v, a: rec.variant };
          }
        }
        if (Object.keys(compact).length > 0) {
          const serialized = JSON.stringify(compact);
          if (serialized.length < 500) pawsExperiments = serialized;
        }
      }

      if (deliveryDate || shipDate || fbp || fbc || pawsExperiments || quizTests.length) {
        const primaryForm = (state.dogForms?.[dogIds[0]] ?? {}) as Partial<DogForm>;
        const primaryPlanDays = Number(primaryForm.selectedPlanDays);

        const updateRes = await fetchCartWithRetry("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            attributes: {
              ...(deliveryDate && {
                __qivora_delivery_date: deliveryDate,
              }),
              ...(shipDate && {
                __qivora_ship_date: shipDate,
              }),
              ...(fbp && { _fbp: fbp }),
              ...(fbc && { _fbc: fbc }),
              ...(pawsExperiments && { _paws_experiments: pawsExperiments }),
              _paws_plan_days: String(primaryPlanDays),
              _paws_number_of_dogs: String(dogIds.length),
              ...quizTestProperties(quizTests, primaryPlanDays),
            },
          }),
        });
        if (!updateRes.ok) {
          console.error("[useCheckout] Failed to update cart attributes", updateRes.status);
          setCheckoutError("Unable to set delivery details. Please try again.");
          setSubmitting(false);
          return;
        }
      }

      const additionalItems: CartLineItem[] = [
        ...allTreatLineItems,
      ];

      if (additionalItems.length > 0) {
        const addOutcome = await addLinesVerified(additionalItems);
        if (!addOutcome.ok) {
          console.error("[useCheckout] Failed to add treat/christmas items", addOutcome.status, addOutcome.text);
          setCheckoutError("Unable to add items to cart. Please try again.");
          setSubmitting(false);
          return;
        }
      }

      // Last line of defence against duplicate subscriptions: strip any
      // repeated (variant, dog) lines that still made it into the cart.
      await removeDuplicateQuizLines();

      try {
        const primaryDogId = dogIds[0];
        const primaryForm = (primaryDogId ? state.dogForms?.[primaryDogId] : null) as Partial<DogForm> | null;
        const trackPlanDays = Number(primaryForm?.selectedPlanDays);

        GTM.trackCheckoutInitiated({
          totalValue: totalOrderValue || 0,
          currency: "GBP",
          numberOfDogs: dogIds.length,
          subscriptionFrequency: trackPlanDays,
          ownerName: primaryForm?.ownerName || undefined,
          ownerEmail: primaryForm?.ownerEmail || undefined,
        });
      } catch { }

      // The quiz is complete and priced into the cart: clear the persisted
      // state so a later visit cannot silently re-add stale dogs/boxes.
      clearSavedQuiz();

      window.location.href = "/checkout";
    } catch (err) {
      console.error("[useCheckout] Checkout error", err);
      setCheckoutError("Something went wrong. Please try again.");
      setSubmitting(false);
    } finally {
      checkoutInFlightRef.current = false;
    }
  };

  const clearErrors = () => {
    setDateError(null);
    setCheckoutError(null);
  };

  return {
    submitting,
    dateError,
    checkoutError,
    goToCheckout,
    clearErrors,
    setDateError,
    mealsUnderRecommended,
    usedPacks,
    recommendedPacks,
    hiddenPriceMap,
  };
}
